import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PlayCircleIcon, ClockIcon, TagIcon } from 'lucide-react';
interface TutorialCardProps {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  completed: boolean;
  completedAt: string | null;
  icon: React.ReactNode;
}
export function TutorialCard({
  id,
  title,
  description,
  category,
  duration,
  completed,
  completedAt,
  icon
}: TutorialCardProps) {
  const navigate = useNavigate();
  // Format the completion date if available
  const formatCompletionDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };
  const completionDate = formatCompletionDate(completedAt);
  const handleCardClick = () => {
    navigate(`/tutorial/${id}`);
  };
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when button is clicked
    navigate(`/tutorial/${id}`);
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 flex flex-col h-full cursor-pointer" onClick={handleCardClick} data-id="element-2454">
      <div className="p-5 flex-grow" data-id="element-2455">
        <div className="flex justify-between items-start mb-3" data-id="element-2456">
          <div className="p-2 rounded-lg bg-gray-50" data-id="element-2457">{icon}</div>
          <div className={`text-xs font-medium px-2 py-1 rounded-full ${completed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-2458">
            {completed ? 'Completed' : 'Not completed'}
          </div>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2459">{title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-2460">{description}</p>
        <div className="flex items-center justify-between" data-id="element-2461">
          <div className="flex items-center text-xs text-gray-500" data-id="element-2462">
            <TagIcon size={14} className="mr-1" data-id="element-2463" />
            {category}
          </div>
          <div className="flex items-center text-xs text-gray-500" data-id="element-2464">
            <ClockIcon size={14} className="mr-1" data-id="element-2465" />
            {duration}
          </div>
        </div>
      </div>
      <div className={`px-5 py-3 border-t border-gray-100 flex justify-between items-center ${completed ? 'bg-green-50' : 'bg-gray-50'}`} data-id="element-2466">
        <button onClick={handleButtonClick} className={`flex items-center text-sm font-medium rounded-md px-3 py-1.5 ${completed ? 'text-green-700 hover:text-green-800' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-2467">
          {completed ? <>
              <CheckCircleIcon size={16} className="mr-1.5" data-id="element-2468" />
              Revisit Tutorial
            </> : <>
              <PlayCircleIcon size={16} className="mr-1.5" data-id="element-2469" />
              Start Tutorial
            </>}
        </button>
        {completed && completionDate && <span className="text-xs text-gray-500" data-id="element-2470">
            Completed {completionDate}
          </span>}
      </div>
    </div>;
}